import {
  Response,
  TMSOrderVo,
  TMSScanOrderVo,
  TMSOrderSearchParams,
  PageResponse,
  PageData,
  TMSScanOrderCreateDo,
  TMSScanPalletCreateDo,
  TMSPalletVo,
  TMSScanOrderPalletVo,
  TMSScanPalletProductVo,
  TMSScanOrderCartonVo,
  TMSScanShippingLabelDo,
  TMSScanOrderShippingLabelUsage,
  TMSScanPalletCompleteDo,
  TMSShippingLabelDraft,
  InstaShipmentReport,
} from "@/types";
import { APIRequest } from "@/utils/api";

export const OrderAPI = new APIRequest({
  baseURL: "/api/wms/v2/tms/orders",
});

export async function queryOrders(
  params: TMSOrderSearchParams,
): Promise<PageData<TMSOrderVo>> {
  return OrderAPI.get<PageResponse<TMSOrderVo>>({
    url: "",
    params,
  }).then((res) => res.data);
}

export async function createOrder(
  data: TMSScanOrderCreateDo,
): Promise<TMSOrderVo> {
  return OrderAPI.post<Response<TMSOrderVo>>({
    url: "/scan",
    data,
  }).then((res) => res.data);
}

export async function getOrder(
  orderIdOrRIIOrderNo: string,
): Promise<TMSScanOrderVo> {
  return OrderAPI.get<Response<TMSScanOrderVo>>({
    url: `/scan/${orderIdOrRIIOrderNo}`,
  }).then((res) => res.data);
}

export async function createScanPallet(
  data: TMSScanPalletCreateDo,
): Promise<TMSPalletVo> {
  return OrderAPI.post<Response<TMSPalletVo>>({
    url: `/scan/${data.orderId}/pallets`,
    data,
  }).then((res) => res.data);
}

export async function getScanOrderPalletById(
  orderId: string,
  palletId: string,
): Promise<TMSScanOrderPalletVo> {
  return OrderAPI.get<Response<TMSScanOrderPalletVo>>({
    url: `/scan/${orderId}/pallets/${palletId}`,
  }).then((res) => res.data);
}

export async function submitScanPalletProduct(
  orderId: string,
  palletId: string,
  sku: string,
  serialNo?: string,
): Promise<TMSScanPalletProductVo> {
  return OrderAPI.post<Response<TMSScanPalletProductVo>>({
    url: `/scan/${orderId}/pallets/${palletId}/products/${sku}`,
    data: { serialNo },
  }).then((res) => res.data);
}

export async function downloadScanPalletCartonLabel(
  orderId: string,
  palletId: string,
  scannedCartonId: number,
): Promise<Blob> {
  return OrderAPI.get<Blob>({
    url: `/scan/${orderId}/pallets/${palletId}/cartons/${scannedCartonId}/label?includeShippingLabel=true`,
    responseType: "blob",
  });
}

export async function downloadScanPalletLabel(
  orderId: string,
  palletId: string,
): Promise<Blob> {
  return OrderAPI.get<Blob>({
    url: `/scan/${orderId}/pallets/${palletId}/label`,
    responseType: "blob",
  });
}

export async function downloadScanShippingLabel(
  params: TMSScanShippingLabelDo,
): Promise<Blob> {
  return OrderAPI.get<Blob>({
    url: `/scan/${params.orderIdOrNo}/shipping/label.pdf`,
    params,
    responseType: "blob",
  });
}

export async function getScanOrderShippingLabelUsage(
  orderId: string,
): Promise<TMSScanOrderShippingLabelUsage> {
  return OrderAPI.get<Response<TMSScanOrderShippingLabelUsage>>({
    url: `/scan/${orderId}/shipping/labels/usage`,
  }).then((res) => res.data);
}

export async function getScanOrderCartons(
  orderId: string,
): Promise<TMSScanOrderCartonVo[]> {
  return OrderAPI.get<Response<TMSScanOrderCartonVo[]>>({
    url: `/scan/${orderId}/cartons`,
  }).then((res) => res.data);
}

export async function deleteScanOrderPallet(
  orderId: string,
  palletId: string,
): Promise<void> {
  return OrderAPI.delete<Response<void>>({
    url: `/scan/${orderId}/pallets/${palletId}`,
  }).then((res) => res.data);
}

export async function deleteScanOrderPalletCarton(
  orderId: string,
  palletId: string,
  scannedCartonId: number,
): Promise<void> {
  return OrderAPI.delete<Response<void>>({
    url: `/scan/${orderId}/pallets/${palletId}/cartons/${scannedCartonId}`,
  }).then((res) => res.data);
}

export async function completeScanOrderPallet(
  orderId: string,
  palletId: string,
  data: TMSScanPalletCompleteDo,
): Promise<void> {
  return OrderAPI.patch<Response<void>>({
    url: `/scan/${orderId}/pallets/${palletId}/complete`,
    data,
  }).then((res) => res.data);
}

export async function uploadScanOrderLabels(
  orderId: string,
  mapping: Record<number, string>,
  labelsFile: File,
): Promise<void> {
  const fd = new FormData();
  fd.append("labels", labelsFile);
  fd.append("payload", JSON.stringify({ labelsPageMapping: mapping }));
  return OrderAPI.patch<Response<void>>({
    url: `/scan/${orderId}/shipping/labels`,
    data: fd,
  }).then((res) => res.data);
}

export async function generateInstaWorksheet(
  orderIds: string[],
): Promise<{ blob: Blob; filename: string }> {
  return OrderAPI.instance
    .post(`/scan/insta/worksheet`, { orderIds }, { responseType: "blob" })
    .then((res) => {
      const disposition = res.headers["content-disposition"] ?? "";
      const match = disposition.match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i);
      const filename = match
        ? decodeURIComponent(match[1].replace(/"/g, ""))
        : "insta-worksheet.pdf";
      return { blob: res.data, filename };
    });
}

export async function getScanOrderShippingLabelDraft(
  orderId: string,
): Promise<TMSShippingLabelDraft> {
  return OrderAPI.get<Response<TMSShippingLabelDraft>>({
    url: `/scan/${orderId}/shipping_label_draft`,
  }).then((res) => res.data);
}

export async function generateScanOrderShippingLabels(
  orderId: string,
): Promise<void> {
  return OrderAPI.post<Response<void>>({
    url: `/scan/${orderId}/shipping_label`,
  }).then((res) => res.data);
}

export async function deleteScanOrderShippingLabels(
  orderId: string,
): Promise<void> {
  return OrderAPI.delete<Response<void>>({
    url: `/scan/${orderId}/shipping_label`,
  }).then((res) => res.data);
}

export async function getScanOrderShipmentReport(
  riiOrderNo: string,
): Promise<InstaShipmentReport> {
  return OrderAPI.get<Response<InstaShipmentReport>>({
    url: `/scan/${riiOrderNo}/shipment/report`,
  }).then((res) => res.data);
}

export async function sendScanOrderShipmentReport(
  riiOrderNo: string,
): Promise<InstaShipmentReport> {
  return OrderAPI.post<Response<InstaShipmentReport>>({
    url: `/scan/${riiOrderNo}/shipment/report`,
  }).then((res) => res.data);
}
